import { useEffect, useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { cn } from '@/shared/lib/cn.ts'

const COPIED_MS = 1600

type CopyState = 'idle' | 'copied' | 'failed'

/** Copies the code for a message to a salon; in a table row it is just an icon and does not open the row. */
export function CopyCodeButton({ code, compact = false }: { code: string; compact?: boolean }) {
  const [state, setState] = useState<CopyState>('idle')

  useEffect(() => {
    if (state === 'idle') return
    const timer = setTimeout(() => setState('idle'), COPIED_MS)
    return () => clearTimeout(timer)
  }, [state])

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code)
      setState('copied')
    } catch {
      setState('failed')
    }
  }

  const label = state === 'copied' ? 'Скопировано' : state === 'failed' ? 'Не скопировалось' : 'Скопировать'
  const Icon = state === 'copied' ? Check : Copy

  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation()
        event.preventDefault()
        void copy()
      }}
      aria-label={compact ? `${label} код ${code}` : undefined}
      title={compact ? label : undefined}
      className={cn(
        'inline-flex shrink-0 items-center justify-center gap-1.5 rounded-control text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink',
        compact ? 'size-9 max-md:size-11' : 'border border-line-strong bg-surface px-3 py-2 text-[13.5px] font-medium text-ink max-md:min-h-11',
        state === 'copied' && 'text-brand-ink',
        state === 'failed' && 'text-warn-ink',
      )}
    >
      <Icon className="size-3.5" aria-hidden />
      {!compact && label}
      {compact && <span aria-live="polite" className="sr-only">{state === 'idle' ? '' : label}</span>}
    </button>
  )
}
